/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';

import { useInlineFieldLabel } from '../_common/form-control';
import { type CommonProps, type HtmlAttributes } from '../_common/types';
import { useSemiControlledInput } from '../_common/use-semi-controlled-input';

type SwitchProps = {
  /** Whether the switch is checked */
  isChecked?: boolean;
  /** Checks the input by default in uncontrolled mode */
  isDefaultChecked?: boolean; // isDefaultChecked sounds weird to me?
  /** Whether the switch is indeterminate */
  isIndeterminate?: boolean;
  /** Whether the switch is disabled */
  isDisabled?: boolean;
  /** Whether the switch & label should take the full available width of its container */
  isFluid?: boolean;
  /** The value of the switch */
  value?: HtmlAttributes<'input'>['value']; // should this be required?
  /** Whether the label should be displayed before the switch */
  hasLeadingLabel?: boolean;
  /** The callback function triggered when the switch value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** The callback function triggered when the switch is clicked */
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
} & (
  | {
      /** The aria label of the switch. Required if the label is not a string. */
      ariaLabel: string;
      // TODO v5: inconsistent naming. Naming the switch takes a union of
      // `label` and `ariaLabel`, so the accessible name comes from a different
      // prop depending on what the label renders. Combobox instead requires the
      // label and clips it with `hasVisuallyHiddenLabel`, and TextInput reaches
      // for htmlAttributes['aria-label'].
      /** The label of the switch. If not a string, supply an aria label for screen reader support. */
      label?: undefined;
    }
  | {
      ariaLabel?: string;
      label: React.ReactNode;
    }
);

export const Switch = ({
  isChecked,
  isDefaultChecked,
  isIndeterminate = false,
  isFluid = false,
  isDisabled = false,
  hasLeadingLabel,
  ariaLabel,
  onChange,
  onClick,
  value,
  label,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'input', SwitchProps>) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const mergedRefs = useMergeRefs([internalRef, ref]);

  const [isCheckedState, internalOnChange] = useSemiControlledInput({
    inputType: 'switch',
    isControlled: isChecked !== undefined,
    onChange,
    state: isChecked ?? isDefaultChecked ?? false,
  });

  const switchClasses = classNames('ndl-switch', className, {
    'ndl-disabled': isDisabled,
  });

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const { labelContent, fieldClassName } = useInlineFieldLabel({
    ariaLabel,
    componentName: 'Switch',
    isDisabled,
    isFluid,
    label,
  });

  return (
    <label className={fieldClassName}>
      {hasLeadingLabel === true && labelContent}
      <input
        ref={mergedRefs}
        type="checkbox"
        role="switch"
        aria-label={ariaLabel}
        disabled={isDisabled}
        checked={isCheckedState}
        onClick={onClick}
        onChange={internalOnChange}
        value={value}
        style={style}
        className={switchClasses}
        {...restProps}
        {...htmlAttributes}
      />
      {Boolean(hasLeadingLabel) === false && labelContent}
    </label>
  );
};
